// ** MUI Components
import {
  Button,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  IconButton
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DataGridPro } from "@mui/x-data-grid-pro";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Third Party Imports
import Draggable from "react-draggable";

// ** Util Imports
import { formatUSCurrency } from "components/CurrencyFormat";

function DraggablePaper(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const TotalOverview = ({ TotalOverviewProps }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const buttons = [
    { name: "Material" },
    { name: "Employee" },
    { name: "Assets" },
    { name: "Other Cost" },
    { name: "Subcontractor" },
  ];

  const {
    open,
    handleCloseTotalOverview,
    filteredColumns,
    filteredRows,
    selectedButton,
    handleFilterTypeChange,
    assetTotalCost,
    materialTotalCost,
    laborTotalCost,
    miscTotalCost,
    subcontractorTotalCost,
    totalCost,
  } = TotalOverviewProps;

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={open}
        onClose={() => handleCloseTotalOverview()}
        PaperComponent={isSmallScreen ? Paper : DraggablePaper}
        aria-labelledby="form-dialog-title"
        PaperProps={{
          sx: {
            height: "100%",
            maxHeight: "95vh",
            width: isSmallScreen ? "95vw" : "auto",
            m: isSmallScreen ? 2 : 0,
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: isSmallScreen ? "flex-end" : "center",
          },
        }}
      >
        <DialogTitle
          style={{ cursor: "move", borderBottom: "1px solid #D9D9D9", paddingBottom: '20px !important' }}
          id="draggable-dialog-title"
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: isSmallScreen ? "column" : "row",
              gap: isSmallScreen ? 1 : 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Typography variant="h3" fontWeight="bold">
                Total Overview
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <IconButton onClick={handleCloseTotalOverview}>
                <Icon icon="tabler:x" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ height: '100%' }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              width: { xs: "100%", md: "auto" },
              mb: 3,
            }}
          >
            {buttons.map((button, index) => (
              <Button
                key={index}
                color={selectedButton === index ? "main" : "secondary"}
                variant={selectedButton === index ? "contained" : "tonal"}
                onClick={() => handleFilterTypeChange(index, button.name)}
              >
                {button.name}
              </Button>
            ))}
          </Box>
          <Box sx={{ height: "100%" }}>
            <DataGridPro
              rowHeight={62}
              rows={filteredRows}
              columns={filteredColumns}
              disableRowSelectionOnClick
              slots={{
                footer: () => null,
              }}
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  textTransform: "none",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "center",
            padding: isSmallScreen ? "16px !important" : "20px 24px !important",
            zIndex: 1,
            flexDirection: isSmallScreen ? "column" : "row",
            backgroundColor: "#fbfbfd",
            borderTop: "1px solid #E5E7EB",
          }}
          className="dialog-actions-dense"
        >
          <Box
            sx={{
              width: "100%",
              backgroundColor: "#f4fef6",
              p: 4,
              border: "1px solid #E5E7EB",
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h4">Total Cost</Typography>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ color: "main.main" }}
              >
                {formatUSCurrency(totalCost || 0)}
              </Typography>
            </Box>
            <Divider sx={{ py: 2 }} />
            <Box sx={{ display: "flex", justifyContent: 'space-between', mt: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h5" color='#6B7280'>Material</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatUSCurrency(materialTotalCost || 0)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h5" color='#6B7280'>Employee</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatUSCurrency(laborTotalCost || 0)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h5" color='#6B7280'>Assets</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatUSCurrency(assetTotalCost || 0)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h5" color='#6B7280'>Other Cost</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatUSCurrency(miscTotalCost || 0)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="h5" color='#6B7280'>Subcontractor</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatUSCurrency(subcontractorTotalCost || 0)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TotalOverview;
